import { MutableRefObject, useCallback, useMemo } from "react";
import { useBalance } from "wagmi";
import {
  ChainDetailType,
  DepositToken,
  WithdrawToken,
} from "../../../interfaces/wallet";

const useEthWalletBalance = (
  chain: ChainDetailType.Item,
  address: MutableRefObject<`0x${string}` | undefined>,
  token: DepositToken | WithdrawToken | undefined
) => {
  const { data: erc20Balance, refetch: erc20Refetch } = useBalance({
    chainId: chain.chainId,
    address: address.current,
    token: token?.address as `0x{string}`,
  });
  const { data: nativeBalance, refetch: nativeRefetch } = useBalance({
    chainId: chain.chainId,
    address: address.current,
  });

  const balance = useMemo(() => {
    if (token) {
      return erc20Balance;
    }
    return nativeBalance;
  }, [erc20Balance, nativeBalance, token]);

  const refetch = useCallback(() => {
    if (token) {
      return erc20Refetch();
    }
    return nativeRefetch();
  }, [erc20Refetch, nativeRefetch, token]);

  const fetchBalance = useCallback(async () => {
    console.log("fetching balance");

    try {
      const res = await refetch();
      console.log("balance fetched", res);
      console.log("chain", chain);
      console.log("address", address.current);
      console.log("token", token);
    } catch (error: any) {
      console.warn("Balance fetch failed:", error?.message || error);

      // Check if this is a persistent connection error that should trigger disconnect
      const isPersistentError =
        error?.code === -32603 ||
        error?.code === -32000 ||
        error?.code === "UNKNOWN_ERROR" ||
        error?.message?.includes("could not coalesce error") ||
        error?.message?.includes("missing trie node") ||
        error?.message?.includes("Internal JSON-RPC error") ||
        error?.message?.includes("network error") ||
        error?.message?.includes("connection failed");

      if (isPersistentError) {
        console.warn(
          "Persistent RPC error detected, triggering wallet disconnect"
        );

        // Show notification via custom event (will be caught by GlobalErrorHandler)
        window.dispatchEvent(
          new CustomEvent("app-error", {
            detail: {
              message:
                "Wallet connection error. Wallet disconnected. Please reconnect your wallet",
              context: "Balance Error",
              isWalletError: true,
            },
          })
        );

        // Also trigger wallet disconnect event for backward compatibility
        window.dispatchEvent(
          new CustomEvent("wallet-disconnect-required", {
            detail: {
              error,
              reason: "Persistent RPC error during balance fetch",
            },
          })
        );
        return;
      }

      // Re-throw other errors that might be more serious
      throw error;
    }
  }, [address, chain, refetch, token]);

  return {
    balance: balance?.formatted,
    fetchBalance,
  };
};

export default useEthWalletBalance;
